import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../hooks/useAuth.js';
import { useNavigate } from 'react-router-dom';
import api from '../services/api.js';
import RecordDetails from '../components/RecordDetails.jsx';
import UploadForm from '../components/UploadForm.jsx';
import RecordsTable from '../components/RecordsTable.jsx';
import DuplicateRecordsTable from '../components/DuplicateRecordsTable.jsx'; // IMPORT ADDED
import ManualRecordForm from '../components/ManualRecordForm.jsx';
import ScheduleCalendar from '../components/ScheduleCalendar.jsx';
import Header from '../components/Header.jsx';
import SummaryTab from '../components/SummaryTab.jsx';
import AssignmentsSummaryTab from '../components/AssignmentSummarytab.jsx';
import RegisterUserForm from '../components/RegisterUserForm.jsx';
import ManageUsers from '../components/ManageUsers.jsx';
import OverdueTasksTab from '../components/OverdueTasksTab.jsx';
import ConfirmationModal from '../components/ConfirmationModal.jsx';
import { Bell, Upload, Edit3, PlusCircle, Calendar, Users, UserPlus, FileText, BarChart, AlertTriangle, ClipboardList } from 'lucide-react';
import Pagination from '../components/Pagination.jsx';
import { selectAssignmentSummary } from '../store/assignmentSummarySlice.js';
import { selectScheduleEvents } from '../store/scheduleCalendarSlice.js';
import {
  assignCollector,
  closeModal,
  createRecord,
  deleteRecords,
  fetchCollectors,
  fetchDuplicates,
  fetchOverdueTasks,
  fetchRecordById,
  fetchRecords,
  mergeDuplicates,
  reassignRecords,
  saveRecord,
  selectAdminCollectors,
  selectAdminCreationMode,
  selectAdminDuplicateRecords,
  selectAdminDuplicatesLoading,
  selectAdminFailedRecords,
  selectAdminFilterCollector,
  selectAdminIsUploading,
  selectAdminLastNotifiedOverdue,
  selectAdminLimit,
  selectAdminLoading,
  selectAdminManualAssignCollector,
  selectAdminMergeModalOpen,
  selectAdminMergePrimaryId,
  selectAdminMergeSubmitting,
  selectAdminMessage,
  selectAdminModal,
  selectAdminOverdueError,
  selectAdminOverdueLoading,
  selectAdminOverdueTasks,
  selectAdminPage,
  selectAdminRecords,
  selectAdminSearchQuery,
  selectAdminSelectedFile,
  selectAdminSelectedRecord,
  selectAdminSelectedRecords,
  selectAdminSeenOverdueCount,
  selectAdminTotalRecords,
  selectAdminUploadCollector,
  selectAdminViewMode,
  setCreationMode,
  setFailedRecords,
  setFilterCollector,
  setLastNotifiedOverdue,
  setLimit,
  setManualAssignCollector,
  setMergeModalOpen,
  setMergePrimaryId,
  setModal,
  setPage,
  setSearchQuery,
  setSelectedFile,
  setSelectedRecord,
  setSelectedRecords,
  setSeenOverdueCount,
  setUploadCollector,
  setViewMode,
  showMessage,
  uploadRecords,
} from '../store/adminSlice.js';

const AdminPage = () => {
  const { user, logout } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const records = useSelector(selectAdminRecords);
  const duplicateRecords = useSelector(selectAdminDuplicateRecords);
  const collectors = useSelector(selectAdminCollectors);
  const loading = useSelector(selectAdminLoading);
  const duplicatesLoading = useSelector(selectAdminDuplicatesLoading);
  const isUploading = useSelector(selectAdminIsUploading);
  const message = useSelector(selectAdminMessage);
  const selectedFile = useSelector(selectAdminSelectedFile);
  const selectedRecord = useSelector(selectAdminSelectedRecord);
  const uploadCollector = useSelector(selectAdminUploadCollector);
  const filterCollector = useSelector(selectAdminFilterCollector);
  const searchQuery = useSelector(selectAdminSearchQuery);
  const selectedRecords = useSelector(selectAdminSelectedRecords);
  const failedRecords = useSelector(selectAdminFailedRecords);
  const modalConfig = useSelector(selectAdminModal);
  const isMergeModalOpen = useSelector(selectAdminMergeModalOpen);
  const mergePrimaryId = useSelector(selectAdminMergePrimaryId);
  const mergeSubmitting = useSelector(selectAdminMergeSubmitting);
  const page = useSelector(selectAdminPage);
  const limit = useSelector(selectAdminLimit);
  const totalRecords = useSelector(selectAdminTotalRecords);
  const creationMode = useSelector(selectAdminCreationMode);
  const manualAssignCollector = useSelector(selectAdminManualAssignCollector);
  const viewMode = useSelector(selectAdminViewMode);
  const overdueTasks = useSelector(selectAdminOverdueTasks);
  const overdueLoading = useSelector(selectAdminOverdueLoading);
  const overdueError = useSelector(selectAdminOverdueError);
  const seenOverdueCount = useSelector(selectAdminSeenOverdueCount);
  const lastNotifiedOverdue = useSelector(selectAdminLastNotifiedOverdue);
  const assignmentSummary = useSelector(selectAssignmentSummary);
  const scheduleEvents = useSelector(selectScheduleEvents);

  useEffect(() => {
    const savedSeen = parseInt(localStorage.getItem('adminSeenOverdueCount') || '0', 10);
    const savedLast = parseInt(localStorage.getItem('adminLastNotifiedOverdue') || '0', 10);
    dispatch(setSeenOverdueCount(savedSeen));
    dispatch(setLastNotifiedOverdue(savedLast));
  }, [dispatch]);

  useEffect(() => {
    localStorage.setItem('adminSeenOverdueCount', String(seenOverdueCount));
  }, [seenOverdueCount]);

  useEffect(() => {
    localStorage.setItem('adminLastNotifiedOverdue', String(lastNotifiedOverdue));
  }, [lastNotifiedOverdue]);

  const notify = useCallback(
    (text, type = 'error', duration = 5000) => {
      dispatch(showMessage(text, type, duration));
    },
    [dispatch]
  );

  const recordCacheRef = useRef({});
  const prefetchedRecordIdsRef = useRef(new Set());

  useEffect(() => {
    records.forEach((record) => {
      if (record?._id) {
        recordCacheRef.current[record._id] = record;
      }
    });
    duplicateRecords.forEach((record) => {
      if (record?._id) {
        recordCacheRef.current[record._id] = record;
      }
    });
  }, [records, duplicateRecords]);

  const lastRecordsQueryRef = useRef('');
  const lastDuplicatesQueryRef = useRef('');

  const loadRecords = useCallback(
    () =>
      dispatch(fetchRecords())
        .unwrap()
        .catch((err) => notify(err?.message || 'Failed to fetch records.')),
    [dispatch, notify]
  );
  const loadDuplicates = useCallback(
    () =>
      dispatch(fetchDuplicates())
        .unwrap()
        .catch((err) => notify(err?.message || 'Failed to fetch duplicate records.')),
    [dispatch, notify]
  );

  // Fetch overdue on Admin/Super Admin
  useEffect(() => {
    const role = String(user?.role || '').toLowerCase();
    if (user && (role === 'admin' || role === 'super_admin')) {
      dispatch(fetchOverdueTasks());
      const intervalId = setInterval(() => dispatch(fetchOverdueTasks()), 300_000);
      return () => clearInterval(intervalId);
    }
    return undefined;
  }, [user, dispatch]);

  useEffect(() => {
    dispatch(setPage(1));
  }, [dispatch, filterCollector, searchQuery]);

  useEffect(() => {
    if (collectors.length > 0) return;
    dispatch(fetchCollectors()).unwrap().catch((err) => {
      console.error('Failed to fetch collectors.', err);
      notify('Could not fetch the list of collectors.');
    });
  }, [dispatch, notify, collectors.length]);

  useEffect(() => {
    if (!selectedRecord) {
      if (viewMode === 'records') {
        const key = `${page}|${limit}|${filterCollector}|${searchQuery}`;
        if (lastRecordsQueryRef.current !== key) {
          lastRecordsQueryRef.current = key;
          loadRecords();
        }
      } else if (viewMode === 'duplicates' && duplicateRecords.length === 0) {
        if (lastDuplicatesQueryRef.current !== 'loaded') {
          lastDuplicatesQueryRef.current = 'loaded';
          loadDuplicates();
        }
      }
    }
  }, [
    loadRecords,
    loadDuplicates,
    selectedRecord,
    viewMode,
    records.length,
    duplicateRecords.length,
    page,
    limit,
    filterCollector,
    searchQuery,
  ]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleFileChange = (e) => {
    dispatch(setSelectedFile(e.target.files[0]));
    dispatch(setFailedRecords([]));
  };

  const handleClearErrors = () => {
    dispatch(setFailedRecords([]));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    try {
      if (!selectedFile) return notify('Please select a file to upload.');
      dispatch(setFailedRecords([]));
      const response = await dispatch(
        uploadRecords({ file: selectedFile, collectorId: uploadCollector })
      ).unwrap();

      // Extract success count and failures from response
      // Supporting both 'failedRecords' and 'errors' naming conventions just in case
      const { count, failedRecords: failures, errors } = response;
      const actualFailures = failures || errors || [];

      if (actualFailures.length > 0) {
        // Set state to trigger the popup in UploadForm
        dispatch(setFailedRecords(actualFailures));
        notify(
          `Uploaded ${count} records. ${actualFailures.length} records failed validation.`,
          'warning'
        );
      } else {
        notify(`Upload processed. ${count} records added successfully.`, 'success');
        dispatch(setFailedRecords([]));
        dispatch(setSelectedFile(null));
        dispatch(setUploadCollector(''));
        const form = document.getElementById('file-upload-form');
        if (form) form.reset();
        await dispatch(fetchRecords());
      }

    } catch (err) {
      // If the entire request fails (e.g. 500 or 400)
      const errorMsg = err?.message || 'File upload failed.';
      notify(errorMsg);
      console.error(err);
    }
  };

  const handleManualCreate = async (formData) => {
    try {
      const payload = {
        ...formData,
        bill: parseFloat(formData.bill) || null,
        crAmount: parseFloat(formData.crAmount) || null,
        claimNo: formData.claimNo.filter(item => item.value),
        adjNumber: formData.adjNumber.filter(item => item.value),
        doi: formData.doi.filter(item => item.value)
      };
      if (manualAssignCollector) payload.assignedCollector = manualAssignCollector;
      await dispatch(createRecord({ payload })).unwrap();
      notify('Record created successfully.', 'success');
      await dispatch(fetchRecords());
      dispatch(setCreationMode('upload'));
      dispatch(setManualAssignCollector(''));
    } catch (err) {
      notify(`Failed to create record: ${err?.message || 'Unknown error'}.`);
      console.error(err.response || err);
    }
  };

  const handleAssignCollector = (recordId, collectorId) => {
    dispatch(
      setModal({
        isOpen: true,
        title: 'Assign Collector',
        message: 'Are you sure you want to assign this collector to the patient?',
        confirmText: 'Assign',
        isDanger: false,
        actionType: 'assignCollector',
        actionPayload: { recordId, collectorId },
      })
    );
  };

  const confirmModalAction = async () => {
    const { actionType, actionPayload } = modalConfig;
    if (!actionType) return;

    try {
      if (actionType === 'assignCollector') {
        await dispatch(assignCollector(actionPayload)).unwrap();
        notify('Collector assigned successfully.', 'success');
      } else if (actionType === 'deleteRecords') {
        await dispatch(deleteRecords(actionPayload)).unwrap();
        notify(`${actionPayload.recordIds.length} record(s) deleted successfully.`, 'success');
        if (viewMode === 'duplicates') {
          await dispatch(fetchDuplicates());
        } else {
          await dispatch(fetchRecords());
        }
      } else if (actionType === 'reassignRecords') {
        await dispatch(reassignRecords(actionPayload)).unwrap();
        notify(`${actionPayload.recordIds.length} record(s) reassigned successfully.`, 'success');
        if (viewMode === 'duplicates') {
          await dispatch(fetchDuplicates());
        } else {
          await dispatch(fetchRecords());
        }
      }
    } catch (err) {
      notify(err?.message || 'Action failed.');
    } finally {
      dispatch(closeModal());
    }
  };

  const handleSaveRecord = async (recordId, data) => {
    try {
      const updated = await dispatch(saveRecord({ recordId, data })).unwrap();
      dispatch(setSelectedRecord(updated));
      return updated;
    } catch (error) {
      notify('Failed to update record.');
      console.error(error);
      throw error;
    }
  };

  const handleAlertSelect = async (recordId) => {
    try {
      const cachedRecord = recordCacheRef.current[recordId];
      if (cachedRecord) {
        dispatch(setSelectedRecord(cachedRecord));
      }
      const record = await dispatch(fetchRecordById(recordId)).unwrap();
      if (record?._id) {
        recordCacheRef.current[record._id] = record;
      }
    } catch (err) {
      console.error('Failed to fetch record from alert:', err);
      notify(err?.message || 'Could not load the selected record.');
    }
  };

  useEffect(() => {
    if (viewMode !== 'overdue' || overdueTasks.length === 0) return;
    overdueTasks.slice(0, 20).forEach((task) => {
      const recordId = task.recordId;
      if (!recordId) return;
      if (recordCacheRef.current[recordId] || prefetchedRecordIdsRef.current.has(recordId)) {
        return;
      }
      prefetchedRecordIdsRef.current.add(recordId);
      dispatch(fetchRecordById(recordId))
        .unwrap()
        .then((record) => {
          if (record?._id) {
            recordCacheRef.current[record._id] = record;
          }
        })
        .catch(() => {});
    });
  }, [dispatch, overdueTasks, viewMode]);

  useEffect(() => {
    if (viewMode !== 'assignments' || assignmentSummary.rows.length === 0) return;
    assignmentSummary.rows.slice(0, 20).forEach((row) => {
      const recordId = row.recordId;
      if (!recordId) return;
      if (recordCacheRef.current[recordId] || prefetchedRecordIdsRef.current.has(recordId)) {
        return;
      }
      prefetchedRecordIdsRef.current.add(recordId);
      dispatch(fetchRecordById(recordId))
        .unwrap()
        .then((record) => {
          if (record?._id) {
            recordCacheRef.current[record._id] = record;
          }
        })
        .catch(() => {});
    });
  }, [dispatch, assignmentSummary.rows, viewMode]);

  useEffect(() => {
    if (viewMode !== 'schedule' || scheduleEvents.length === 0) return;
    scheduleEvents.slice(0, 20).forEach((event) => {
      const recordId = event?.recordId;
      if (!recordId) return;
      if (recordCacheRef.current[recordId] || prefetchedRecordIdsRef.current.has(recordId)) {
        return;
      }
      prefetchedRecordIdsRef.current.add(recordId);
      dispatch(fetchRecordById(recordId))
        .unwrap()
        .then((record) => {
          if (record?._id) {
            recordCacheRef.current[record._id] = record;
          }
        })
        .catch(() => {});
    });
  }, [dispatch, scheduleEvents, viewMode]);

  const handleSelectionChange = (recordId) => {
    dispatch(
      setSelectedRecords(
        selectedRecords.includes(recordId)
          ? selectedRecords.filter((id) => id !== recordId)
          : [...selectedRecords, recordId]
      )
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const source = viewMode === 'duplicates' ? duplicateRecords : records;
      dispatch(setSelectedRecords(source.map((r) => r._id)));
    } else {
      dispatch(setSelectedRecords([]));
    }
  };

  const handleDeleteSelectedRecords = () => {
    if (selectedRecords.length === 0) {
      return notify('Please select at least one record to delete.', 'error');
    }

    dispatch(
      setModal({
        isOpen: true,
        title: 'Delete Records',
        message: `Are you sure you want to delete ${selectedRecords.length} record(s)? This action cannot be undone.`,
        confirmText: 'Delete',
        isDanger: true,
        actionType: 'deleteRecords',
        actionPayload: { recordIds: selectedRecords },
      })
    );
  };

  const handleReassignRecords = (collectorId) => {
    if (selectedRecords.length === 0) {
      return notify('Please select at least one record to reassign.', 'error');
    }

    dispatch(
      setModal({
        isOpen: true,
        title: 'Reassign Records',
        message: `Are you sure you want to reassign ${selectedRecords.length} record(s)?`,
        confirmText: 'Reassign',
        isDanger: false,
        actionType: 'reassignRecords',
        actionPayload: { recordIds: selectedRecords, collectorId },
      })
    );
  };
  // --- MERGE SELECTED DUPLICATES ---
  const openMergeSelectedModal = () => {
    if (viewMode !== 'duplicates') {
      return notify('Merge is only available in the Duplicate Records tab.', 'error');
    }
    if (!selectedRecords || selectedRecords.length < 2) {
      return notify('Please select at least 2 duplicate records to merge.', 'error');
    }
    // default primary = first selected
    dispatch(setMergePrimaryId(selectedRecords[0]));
    dispatch(setMergeModalOpen(true));
  };

  const confirmMergeSelected = async () => {
    if (!mergePrimaryId) {
      return notify('Please choose a primary record (the one to keep).', 'error');
    }

    const duplicateIds = selectedRecords.filter((id) => id !== mergePrimaryId);
    if (duplicateIds.length === 0) {
      return notify('Primary record cannot be the only selected record.', 'error');
    }

    try {
      const res = await dispatch(
        mergeDuplicates({ primaryId: mergePrimaryId, duplicateIds })
      ).unwrap();

      notify(
        `Merge completed. Primary kept: ${mergePrimaryId}. Duplicates merged: ${res.result?.mergedRecords ?? duplicateIds.length}. Comments merged: ${res.result?.mergedComments ?? 0}.`,
        'success'
      );

      await dispatch(fetchDuplicates());
    } catch (err) {
      console.error('Merge failed:', err);
      notify(err?.message || 'Merge failed. Check backend logs for details.');
    }
  };
  const navItems = [
    { label: 'Create Patient', icon: <PlusCircle className="w-4 h-4" />, mode: 'create' },
    { label: 'All Patients', icon: <FileText className="w-4 h-4" />, mode: 'records' },
    { label: 'Duplicate Records', icon: <AlertTriangle className="w-4 h-4" />, mode: 'duplicates' },
    { label: 'Schedule Calendar', icon: <Calendar className="w-4 h-4" />, mode: 'schedule' },
    { label: 'Register User', icon: <UserPlus className="w-4 h-4" />, mode: 'register' },
    { label: 'Manage Users', icon: <Users className="w-4 h-4" />, mode: 'users' },
    { label: 'Overdue Alerts', icon: <Bell className="w-4 h-4" />, mode: 'overdue' },
    { label: 'Summary', icon: <BarChart className="w-4 h-4" />, mode: 'summary' },
    { label: 'Assignments', icon: <ClipboardList className="w-4 h-4" />, mode: 'assignments' },
  ];

  const newOverdueCount = Math.max(0, overdueTasks.length - seenOverdueCount);

  useEffect(() => {
    const currentTotal = overdueTasks.length;
    const unseen = Math.max(0, currentTotal - seenOverdueCount);

    if (unseen > 0 && currentTotal > lastNotifiedOverdue && viewMode !== 'overdue') {
      notify(
        `${unseen} overdue task${unseen > 1 ? 's' : ''} need your attention. Go to “Overdue Alerts” to review.`,
        'error',
        7000
      );
      dispatch(setLastNotifiedOverdue(currentTotal));
    }
  }, [overdueTasks.length, seenOverdueCount, viewMode, lastNotifiedOverdue, dispatch, notify]);

  const collectorsForDropdown = [{ _id: 'unassigned', username: 'Unassigned' }, ...collectors];

  if (selectedRecord) {
    return (
      <>
        <Header
          title="Admin Dashboard"
          user={user}
          onLogout={handleLogout}
          navItems={navItems}
          currentViewMode={'records'}
          onViewModeChange={(mode) => {
            dispatch(setSelectedRecord(null));
            dispatch(setViewMode(mode));
            if (mode === 'overdue') {
              dispatch(setSeenOverdueCount(overdueTasks.length));
            }
          }}
          overdueTaskCount={newOverdueCount}
        />
        <RecordDetails
          record={selectedRecord}
          onBack={() => dispatch(setSelectedRecord(null))}
          onSave={handleSaveRecord}
          onAssign={handleAssignCollector}
          collectors={collectorsForDropdown}
          user={user}
          api={api}
        />
        <ConfirmationModal 
          isOpen={modalConfig.isOpen}
          onClose={() => dispatch(closeModal())}
          onConfirm={confirmModalAction}
          title={modalConfig.title}
          message={modalConfig.message}
          confirmText={modalConfig.confirmText}
          isDanger={modalConfig.isDanger}
        />
      </>
    );
  }

  return (
    <>
      <Header
        title="Admin Dashboard"
        user={user}
        onLogout={handleLogout}
        navItems={navItems}
        currentViewMode={viewMode}
        onViewModeChange={(mode) => {
          dispatch(setViewMode(mode));
          if (mode === 'overdue') {
            dispatch(setSeenOverdueCount(overdueTasks.length));
          }
        }}
        overdueTaskCount={newOverdueCount}
      />

      <ConfirmationModal 
        isOpen={modalConfig.isOpen}
        onClose={() => dispatch(closeModal())}
        onConfirm={confirmModalAction}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        isDanger={modalConfig.isDanger}
      />
      {/* Merge Selected Duplicates Modal */}
      {isMergeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-bold text-gray-800">Merge Selected Duplicates</h3>
              <p className="text-sm text-gray-600 mt-1">
                Choose which record to keep as the <strong>Primary</strong>. All comments from the others will be copied into it, tagged with source info, then the duplicates will be deleted.
              </p>
            </div>

            <div className="px-6 py-4 max-h-[60vh] overflow-auto">
              <div className="space-y-3">
                {selectedRecords.map((id) => {
                  const r = (duplicateRecords || []).find((x) => String(x._id) === String(id));
                  const label = r
                    ? `${r.provider || 'N/A'} | ${r.ptName || 'N/A'} | ADJ: ${(r.adjNumber || []).map((a) => a.value).join(', ') || 'N/A'} | Collector: ${r.assignedCollector?.username || 'Unassigned'}`
                    : id;
                  return (
                    <label key={id} className="flex items-start gap-3 rounded-lg border p-3 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="mergePrimary"
                        className="mt-1 h-4 w-4"
                        checked={mergePrimaryId === id}
                        onChange={() => dispatch(setMergePrimaryId(id))}
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-800 break-all">{id}</div>
                        <div className="text-sm text-gray-600 break-words">{label}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="border-t px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => dispatch(setMergeModalOpen(false))}
                disabled={mergeSubmitting}
                className="px-4 py-2 rounded-md border bg-white hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={confirmMergeSelected}
                disabled={mergeSubmitting}
                className="px-4 py-2 rounded-md bg-green-600 text-white font-semibold hover:bg-green-700 disabled:bg-gray-400"
              >
                {mergeSubmitting ? 'Merging...' : 'Merge Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 font-sans">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          {message.text && (
            <div
              className={`mb-6 p-4 rounded-xl border-l-4 ${
                message.type === 'success'
                  ? 'bg-green-50 border-green-500 text-green-800'
                  : 'bg-red-50 border-red-500 text-red-800'
              }`}
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  {message.type === 'success' ? (
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="ml-3">
                  <p className="font-medium">{message.text}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl overflow-hidden">
            {viewMode === 'create' && (
              <>
                <div className="border-b border-gray-200 bg-gray-50/50">
                  <div className="flex">
                    <button
                      onClick={() => dispatch(setCreationMode('upload'))}
                      className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm transition-all duration-200 ${
                        creationMode === 'upload'
                          ? 'border-blue-500 text-blue-600 bg-white '
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-white/50'
                      }`}
                    >
                      <Upload className="h-4 w-4" />
                      <span>Bulk Upload</span>
                    </button>
                    <button
                      onClick={() => dispatch(setCreationMode('manual'))}
                      className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm transition-all duration-200 ${
                        creationMode === 'manual'
                          ? 'border-blue-500 text-blue-600 bg-white '
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-white/50'
                      }`}
                    >
                      <Edit3 className="h-4 w-4" />
                      <span>Manual Entry</span>
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {creationMode === 'upload' ? (
                    <UploadForm
                      onFileChange={handleFileChange}
                      onUpload={handleUpload}
                      collectors={collectors}
                      uploadCollector={uploadCollector}
                      setUploadCollector={(value) => dispatch(setUploadCollector(value))}
                      isUploading={isUploading}
                      failedRecords={failedRecords} // PASS FAILED RECORDS
                      onClearErrors={handleClearErrors} // PASS CLOSE HANDLER
                    />
                  ) : (
                    <ManualRecordForm
                      onManualCreate={handleManualCreate}
                      collectors={collectors}
                      assignedCollector={manualAssignCollector}
                      setAssignedCollector={(value) => dispatch(setManualAssignCollector(value))}
                    />
                  )}
                </div>
              </>
            )}

            {viewMode === 'records' && (
              <div className="p-6">
                <RecordsTable
                  records={records}
                  collectors={collectorsForDropdown}
                  filterCollector={filterCollector}
                  setFilterCollector={(value) => dispatch(setFilterCollector(value))}
                  searchQuery={searchQuery}
                  setSearchQuery={(value) => dispatch(setSearchQuery(value))}
                  onRecordSelect={(record) => dispatch(setSelectedRecord(record))}
                  loading={loading}
                  selectedRecords={selectedRecords}
                  onSelectionChange={handleSelectionChange}
                  onSelectAll={handleSelectAll}
                  onDeleteSelected={handleDeleteSelectedRecords}
                  onReassign={handleReassignRecords}
                  userRole={user?.role}
                  api={api}
                />

                <Pagination
                  page={page}
                  setPage={(value) => dispatch(setPage(value))}
                  limit={limit}
                  setLimit={(value) => dispatch(setLimit(value))}
                  total={totalRecords}
                />
              </div>
            )}

            {viewMode === 'duplicates' && (
              <div className="p-6">
                <DuplicateRecordsTable
                  records={duplicateRecords}
                  collectors={collectorsForDropdown}
                  filterCollector={filterCollector}
                  setFilterCollector={(value) => dispatch(setFilterCollector(value))}
                  searchQuery={searchQuery}
                  setSearchQuery={(value) => dispatch(setSearchQuery(value))}
                  onRecordSelect={(record) => dispatch(setSelectedRecord(record))}
                  loading={duplicatesLoading}
                  selectedRecords={selectedRecords}
                  onSelectionChange={handleSelectionChange}
                  onSelectAll={handleSelectAll}
                  onDeleteSelected={handleDeleteSelectedRecords}
                  onReassign={handleReassignRecords}
                  onMergeSelected={openMergeSelectedModal}                 
                  userRole={user?.role}
                />
              </div>
            )}

            {viewMode === 'schedule' && (
              <div className="p-6">
                <ScheduleCalendar
                  user={user}
                  onRecordSelect={(record) => dispatch(setSelectedRecord(record))}
                  api={api}
                  recordCache={recordCacheRef.current}
                />
              </div>
            )}

            {viewMode === 'register' && (
              <div className="p-6">
                <RegisterUserForm />
              </div>
            )}

            {viewMode === 'users' && (
              <div className="p-6">
                <ManageUsers />
              </div>
            )}

            {viewMode === 'overdue' && (
              <div className="p-6">
                <OverdueTasksTab
                  tasks={overdueTasks}
                  onRecordSelect={handleAlertSelect}
                  loading={overdueLoading}
                  error={overdueError}
                />
              </div>
            )}

            {viewMode === 'summary' && (
              <div className="p-6">
                <SummaryTab />
              </div>
            )}

            {viewMode === 'assignments' && (
              <div className="p-6">
          <AssignmentsSummaryTab onRecordSelect={handleAlertSelect} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminPage;
